/**
 * 通用维度趋势组件
 * 支持：举报类型/子话题/语言/渠道等维度
 * 图表类型：折线图、柱状图、饼图、表格
 */
import React, { useCallback, useMemo, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import { lineIcon, barIcon, pieIcon, tableIcon } from '@op/constants/chart';
import useLanguage from '@op/hooks/useLanguage';
import { DBO_U } from '@op/utils/format';
import { dealWithOthersName } from '@op/utils/languageValue';
import { channelInfo } from '@op/components/ChannelLabel';
import { useTrendData } from '../../hooks/useTrendData';
import { formatContributionText } from '../../utils/calculations';
import TrendChart from './TrendChart';
import PieChart from './PieChart';
import TableChart from './TableChart';
import './index.scss';

type TDimensionType = 'language' | 'channel';

type TChartType = 'line' | 'bar' | 'pie' | 'table';

interface IDimensionTrendProps {
  filter: any;
  dimension: TDimensionType;
}

const DimensionTrend: React.FC<IDimensionTrendProps> = ({
  filter,
  dimension,
}) => {

  const { formatMessage } = useLanguage();
  const [chartType, setChartType] = useState<TChartType>('line');

  // 使用新的 useTrendData hook，获取图表数据
  const { loading, lineChartData, pieChartData, summary } = useTrendData({
    filter,
    dimension,
  });

  // 获取显示名称
  const getDisplayName = useCallback((name: string) => {
    return dealWithOthersName(name, dimension);
  }, [formatMessage]);

  // 标题配置
  const titleConfig = useMemo(() => {
    const configs: Record<TDimensionType, any> = {
      language: {
        title: formatMessage({ id: 'Mentions Trend by Language' }),
        downloadKey: 'WeeklyLanguage',
        downloadFileName: 'Weekly Report - Language Trend',
        logBtnId: 'WR_Language',
      },
      channel: {
        title: formatMessage({ id: 'Mentions Trend by Channel' }),
        downloadKey: 'WeeklyChannel',
        downloadFileName: 'Weekly Report - Channel Trend',
        logBtnId: 'WR_Channel',
      },
    };
    return configs[dimension];
  }, [dimension, formatMessage]);

  // 图表操作按钮配置
  const operations = useMemo(() => {
    return [pieIcon, barIcon, lineIcon, tableIcon];
  }, []);

  // 汇总信息文本 - 使用通用函数
  const summaryText = useMemo(() => {
    if (!summary?.top_contributor) return '';
    const top = summary.top_contributor;
    const dimensionValue = getDisplayName(top.dimension_value);
    const labelPrefix = formatMessage({ id: dimension === 'channel' ? 'Channel' : 'Language' });

    return formatContributionText({
      categoryName: dimensionValue,
      changeRate: top.volume_ratio,
      contributionRate: top.contribution_rate,
      labelPrefix,
      formatMessage,
    });
  }, [summary, getDisplayName, formatMessage, dimension]);

  // 下载处理
  const handleDownload = () => {
    if (!lineChartData) return;
    DBO_U.download({
      key: titleConfig.downloadKey,
      fileName: titleConfig.downloadFileName,
      filter,
      data: lineChartData.tableRecord,
      auxiliaryParams: { filter, dimension },
    });
  };

  // 根据图表类型渲染对应组件
  const renderContent = () => {
    if (chartType === 'table') {
      return (
        <TableChart data={lineChartData} getDisplayName={getDisplayName} />
      );
    }

    if (chartType === 'pie') {
      return (
        <PieChart
          data={pieChartData}
          dimension={dimension}
          getDisplayName={getDisplayName}
          filter={filter}
        />
      );
    }

    // line, bar
    return (
      <TrendChart
        data={lineChartData}
        chartType={chartType}
        dimension={dimension}
        getDisplayName={getDisplayName}
        filter={filter}
      />
    );
  };
  return (
    <CardCommon
      className={`dbo-dimension-trend dbo-dimension-trend--${dimension}`}
      title={titleConfig.title}
      operations={operations}
      defaultOperation={chartType}
      handleOperationsOnClick={(item) => setChartType(item?.symbol as 'line')}
      loading={loading}
      empty={!lineChartData || lineChartData?.isEmpty}
      loadingHeight={350}
      emptyHeight={350}
      hasDownload={!lineChartData?.isEmpty}
      handleDownloadOnClick={handleDownload}
      logBtnId={titleConfig.logBtnId}
    >
      {summaryText && <div className="echarts-tip-text">{summaryText}</div>}
      {renderContent()}
    </CardCommon>
  );
};

export default DimensionTrend;
